import React, { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  useCannedResponses,
  useReorderCannedResponses,
} from "./hooks/canned-responses.hooks";
import { useHotel } from "@/hooks/useHotel";
import { Button } from "@/components/ui/button";
import { CannedResponseItem } from "./CannedResponseItem";
import { CreateCannedResponseItem } from "./CreateCannedResponseItem";
import { cn } from "@/lib/utils";
import { ReorderEntityType } from "@/services/interfaces/ICategoryService";

interface CannedResponseListSectionProps {
  categoryId: string;
  className?: string;
}

export const CannedResponseListSection: React.FC<
  CannedResponseListSectionProps
> = ({ categoryId, className }) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const limit = 7;

  const { data, isLoading } = useCannedResponses({
    categoryId,
    hotelId: hotelId || "",
    page,
    limit,
  });

  const reorderCannedResponses = useReorderCannedResponses();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && data?.items && hotelId) {
      const oldIndex = data.items.findIndex((item) => item.id === active.id);
      const newIndex = data.items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(data.items, oldIndex, newIndex);

      reorderCannedResponses.mutate({
        ids: newItems.map((item) => item.id),
        hotelId,
        type: ReorderEntityType.CANNED_RESPONSE,
        startIndex: (page - 1) * limit,
      });
    }
  };

  return (
    <div
      className={cn(
        "border-base-border relative flex h-full flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-black/96">
          {t("chats.cannedResponsesManagement.title")}
        </h2>
        <Button
          variant="new_default"
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
        >
          <Plus />
          {t("chats.cannedResponsesManagement.create")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            {t("common.loading")}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={data?.items.map((i) => i.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-2">
                {isCreating && (
                  <CreateCannedResponseItem
                    categoryId={categoryId}
                    onCancel={() => setIsCreating(false)}
                  />
                )}
                {data?.items.map((item) => (
                  <CannedResponseItem key={item.id} response={item} />
                ))}
                {!isLoading && data?.items.length === 0 && !isCreating && (
                  <div className="text-muted-foreground py-8 text-center">
                    {t("chats.cannedResponsesManagement.empty")}
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <Pagination className="border-base-border mt-auto border-t pt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              className={cn(
                "cursor-pointer",
                page === 1 && "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(Math.max(1, page - 1))}
            />
          </PaginationItem>
          {data &&
            Array.from({ length: data.totalPages }, (_, i) => i + 1).map(
              (p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={page === p}
                    onClick={() => setPage(p)}
                    className="cursor-pointer"
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
          <PaginationItem>
            <PaginationNext
              className={cn(
                "cursor-pointer",
                (!data || page === data.totalPages) &&
                  "pointer-events-none opacity-50",
              )}
              onClick={() => setPage(page + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
